import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FilterContext";
import { PageHeader, UnitPicker } from "@/components/Shared";

const ALL = "__all";
const AUTO = "__auto";
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const periodOptions = () => {
  const d = new Date(Date.now() - 864e5);
  return [0, 1, 2, 3].map((i) => {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return { value: `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`, label: `${BULAN[x.getMonth()]} ${x.getFullYear()}${i === 0 ? " (berjalan)" : ""}` };
  });
};

function PeriodSelect({ value, onChange }) {
  const opts = useMemo(periodOptions, []);
  return (
    <Select value={value ?? AUTO} onValueChange={(v) => onChange(v === AUTO ? null : v)}>
      <SelectTrigger data-testid="wa-period-select" className="w-60"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={AUTO}>Periode otomatis (terbaru)</SelectItem>
        {opts.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`wa-period-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
const LOG_CLS = { simulasi: "text-sky-700 bg-sky-50", terkirim: "text-emerald-700 bg-emerald-50", gagal: "text-rose-700 bg-rose-50" };

function DeviceCard({ settings, onSaved }) {
  const [dev, setDev] = useState("");
  const save = async () => {
    try { onSaved((await api.put("/wa/device", { device_id: dev })).data); setDev(""); toast.success("Device ID disimpan"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="wa-device-card" className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="font-heading text-lg font-semibold">Device Whacenter</h3>
      <p className="text-xs text-muted-foreground">
        {settings.device_configured ? <>Terhubung ke device <b className="font-num">{settings.device_masked}</b> (status harus CONNECTED di Whacenter)</> : "Belum ada device ID — pesan hanya disimulasikan."}
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Input data-testid="wa-device-input" type="password" autoComplete="off" placeholder={settings.device_configured ? "Isi untuk mengganti device ID" : "Device ID Whacenter"} value={dev} onChange={(e) => setDev(e.target.value)} />
        <Button data-testid="wa-device-save" onClick={save} disabled={!dev.trim()}>Simpan</Button>
      </div>
    </div>
  );
}

function SettingsCard({ settings, isAdmin, onSaved }) {
  const save = async (patch) => {
    try { onSaved((await api.put("/wa/settings", { frequency: settings.frequency, enabled: settings.enabled, hours: settings.hours, ...patch })).data); toast.success("Pengaturan disimpan"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="font-heading text-lg font-semibold">Jadwal Ringkasan</h3>
      <p className="text-xs text-muted-foreground">{settings.schedule} · terakhir otomatis: {fmtDate(settings.last_auto_run)}</p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Select value={settings.frequency} onValueChange={(v) => save({ frequency: v })} disabled={!isAdmin}>
          <SelectTrigger data-testid="wa-frequency-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="harian">Harian</SelectItem><SelectItem value="mingguan">Mingguan (Senin)</SelectItem><SelectItem value="bulanan">Bulanan (tgl 1)</SelectItem></SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="wa-enabled-switch" checked={settings.enabled} onCheckedChange={(v) => save({ enabled: v })} disabled={!isAdmin} />Aktif</label>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jam kirim (WIB) — boleh lebih dari satu</p>
      <div className="mt-2 grid grid-cols-6 gap-1.5 sm:grid-cols-8 lg:grid-cols-12">
        {[...Array(24).keys()].map((h) => {
          const on = settings.hours.includes(h);
          const next = on ? settings.hours.filter((x) => x !== h) : [...settings.hours, h];
          return (
            <button key={h} type="button" data-testid={`wa-hour-${h}`} disabled={!isAdmin || (on && settings.hours.length === 1)} onClick={() => save({ hours: next })}
              className={`rounded-md border px-1 py-1.5 font-num text-xs transition-colors disabled:cursor-not-allowed ${on ? "border-emerald-600 bg-emerald-600 text-white" : "hover:bg-muted"}`}>
              {String(h).padStart(2, "0")}:00
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilesCard({ isAdmin }) {
  const [files, setFiles] = useState(null);
  const load = useCallback(() => { api.get("/wa/files").then((r) => setFiles(r.data)).catch(() => setFiles([])); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (f) => {
    if (!window.confirm(`Hapus ${f.name}? Link yang sudah terkirim tidak bisa dibuka lagi.`)) return;
    try { await api.delete(`/wa/files/${f.id}`); toast.success("File dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const copy = async (f) => {
    try { await navigator.clipboard.writeText(f.url); toast.success("Link disalin"); } catch { toast.error("Browser menolak akses clipboard"); }
  };
  return (
    <div data-testid="wa-files-card" className="overflow-x-auto rounded-xl border bg-card shadow-sm">
      <div className="border-b p-4"><h3 className="font-heading text-lg font-semibold">Arsip Laporan PPT</h3><p className="text-xs text-muted-foreground">Semua link PPT yang pernah dikirim lewat laporan bulanan. Link berlaku permanen.</p></div>
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="p-3">Dibuat</th><th className="p-3">Jenis</th><th className="p-3">Unit</th><th className="p-3">Periode</th><th className="p-3">File</th><th className="p-3" /></tr></thead>
        <tbody>
          {files === null && <tr><td colSpan={6} className="p-4 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>}
          {files?.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Belum ada PPT yang dikirim.</td></tr>}
          {files?.map((f) => (
            <tr key={f.id} data-testid="wa-file-row" className="border-b">
              <td className="p-3 whitespace-nowrap">{fmtDate(f.created_at)}</td>
              <td className="p-3">{f.kind === "rapor" ? "Rapor Unit" : "Laporan"}</td>
              <td className="p-3">{f.unit || `Seluruh Unit${f.uid ? ` ${f.uid}` : ""}`}</td>
              <td className="p-3 whitespace-nowrap">{f.periode || "-"}</td>
              <td className="p-3 text-xs">{f.name}</td>
              <td className="p-3"><div className="flex justify-end gap-1">
                <Button data-testid={`wa-file-copy-${f.id}`} size="sm" variant="outline" onClick={() => copy(f)}>Salin link</Button>
                <Button asChild size="sm" variant="ghost"><a data-testid={`wa-file-open-${f.id}`} href={f.url}>Unduh</a></Button>
                {isAdmin && <Button data-testid={`wa-file-delete-${f.id}`} size="icon" variant="ghost" onClick={() => remove(f)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlyCard({ settings, isAdmin, onSaved }) {
  const save = async (patch) => {
    const body = { monthly_enabled: settings.monthly_enabled, monthly_day: settings.monthly_day, monthly_hour: settings.monthly_hour, ...patch };
    try { onSaved((await api.put("/wa/monthly", body)).data); toast.success("Jadwal bulanan disimpan"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="wa-monthly-card" className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="font-heading text-lg font-semibold">Laporan Bulanan + PPT</h3>
      <p className="text-xs text-muted-foreground">Ringkasan akumulasi YANTEK + link unduh PPT Laporan Akumulasi (berlaku permanen) · {settings.monthly_schedule} · terakhir: {fmtDate(settings.last_monthly_run)}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Select value={String(settings.monthly_day)} onValueChange={(v) => save({ monthly_day: Number(v) })} disabled={!isAdmin}>
          <SelectTrigger data-testid="wa-monthly-day" className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>{[...Array(28).keys()].map((d) => <SelectItem key={d} value={String(d + 1)}>Tanggal {d + 1}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(settings.monthly_hour)} onValueChange={(v) => save({ monthly_hour: Number(v) })} disabled={!isAdmin}>
          <SelectTrigger data-testid="wa-monthly-hour" className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{[...Array(24).keys()].map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00 WIB</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="wa-monthly-switch" checked={settings.monthly_enabled} onCheckedChange={(v) => save({ monthly_enabled: v })} disabled={!isAdmin} />Aktif</label>
      </div>
    </div>
  );
}

const UidSelect = ({ value, onChange, testid, className = "w-40" }) => {
  const { meta } = useFilters();
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)}>
      <SelectTrigger data-testid={testid} className={className}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>Semua UID</SelectItem>{(meta?.uids || []).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
    </Select>
  );
};

function Preview({ ym }) {
  const { meta } = useFilters();
  const [unit, setUnit] = useState(null);
  const [uid, setUid] = useState(null);
  const [kind, setKind] = useState("harian");
  const [text, setText] = useState("");
  const units = useMemo(() => [...new Set((meta?.units || []).filter((u) => !uid || u.uid === uid).map((u) => u.unit))], [meta, uid]);
  useEffect(() => { setText(""); api.get("/wa/preview", { params: { kind, ...(unit ? { unit } : {}), ...(uid ? { uid } : {}), ...(ym ? { ym } : {}) } }).then((r) => setText(r.data.text)); }, [unit, uid, kind, ym]);
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-lg font-semibold">Pratinjau Pesan</h3>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="wa-preview-kind-select" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="harian">Laporan Harian</SelectItem><SelectItem value="bulanan">Laporan Bulanan</SelectItem></SelectContent>
        </Select>
        <UidSelect testid="wa-preview-uid-select" value={uid} onChange={(v) => { setUid(v); setUnit(null); }} />
        <Select value={unit ?? ALL} onValueChange={(v) => setUnit(v === ALL ? null : v)}>
          <SelectTrigger data-testid="wa-preview-unit-select" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Seluruh Unit</SelectItem>{units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <pre data-testid="wa-preview-text" className="mt-4 whitespace-pre-wrap rounded-lg bg-[#E7F7EC] p-4 font-sans text-sm text-slate-800 dark:bg-emerald-950/40 dark:text-emerald-100">{text || "Memuat…"}</pre>
    </div>
  );
}

const EMPTY = { name: "", type: "personal", target: "", uid: null, units: [], reports: ["harian"], active: true };
const KINDS = [["harian", "Harian"], ["bulanan", "Bulanan + PPT"]];

const KindChips = ({ value, onChange, disabled, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {KINDS.map(([k, l]) => {
      const on = value.includes(k);
      return (
        <button key={k} type="button" data-testid={`${testid}-${k}`} disabled={disabled || (on && value.length === 1)}
          onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])}
          className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed ${on ? "border-emerald-600 bg-emerald-600 text-white" : "text-muted-foreground hover:bg-muted"}`}>{l}</button>
      );
    })}
  </div>
);

function RecipientDialog({ open, initial, onClose, onSaved }) {
  const { meta } = useFilters();
  const [form, setForm] = useState(() => (initial ? { name: initial.name, type: initial.type, target: initial.target, uid: initial.uid,
    units: initial.units, reports: initial.reports, active: initial.active } : EMPTY));
  const save = async () => {
    try {
      if (initial) await api.put(`/wa/recipients/${initial.id}`, form);
      else await api.post("/wa/recipients", form);
      toast.success(initial ? "Penerima diperbarui" : "Penerima ditambahkan"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const grup = form.type === "grup";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial ? "Edit Penerima WhatsApp" : "Tambah Penerima WhatsApp"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label>Nama</Label><Input data-testid="recipient-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid gap-1.5"><Label>Tipe</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, target: "" })}>
              <SelectTrigger data-testid="recipient-form-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="personal">Personal (nomor)</SelectItem><SelectItem value="grup">Grup WhatsApp</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><Label>{grup ? "Nama grup (persis, huruf besar/kecil) atau ID grup …@g.us" : "Nomor WhatsApp (08xxx atau 628xxx)"}</Label>
            <Input data-testid="recipient-form-target" placeholder={grup ? "Tim Kinerja Distribusi" : "081234567890"} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
          <div className="grid gap-1.5"><Label>UID (kosong = semua UID)</Label><UidSelect testid="recipient-form-uid" className="w-full" value={form.uid} onChange={(uid) => setForm({ ...form, uid, units: uid ? form.units.filter((u) => (meta?.units || []).some((m) => m.unit === u && m.uid === uid)) : form.units })} /></div>
          <div className="grid gap-1.5"><Label>Unit ringkasan (kosong = ringkasan seluruh unit)</Label><UnitPicker testid="recipient-form-units" uid={form.uid} value={form.units} onChange={(units) => setForm({ ...form, units })} /></div>
          <div className="grid gap-1.5"><Label>Jenis laporan</Label><KindChips testid="recipient-form-report" value={form.reports} onChange={(reports) => setForm({ ...form, reports })} /></div>
        </div>
        <DialogFooter><Button data-testid="recipient-form-save" onClick={save}>Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WhatsAppPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [settings, setSettings] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [logs, setLogs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  const [ym, setYm] = useState(null);
  const load = useCallback(() => {
    api.get("/wa/settings").then((r) => setSettings(r.data));
    api.get("/wa/recipients").then((r) => setRecipients(r.data));
    api.get("/wa/logs").then((r) => setLogs(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async (kind) => {
    setSending(kind);
    try { const { data } = await api.post("/wa/send-now", null, { params: { kind, ...(ym ? { ym } : {}) } }); toast.success(`${data.sent} penerima laporan ${kind} diproses di latar belakang (mode ${data.mode}). Cek Log Pengiriman sebentar lagi.`); setTimeout(load, kind === "bulanan" ? 30000 : 8000); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  const setReports = async (r, reports) => {
    try { await api.put(`/wa/recipients/${r.id}`, { name: r.name, type: r.type, target: r.target, uid: r.uid, units: r.units, reports, active: r.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const test = async (r) => {
    try { const { data } = await api.post(`/wa/recipients/${r.id}/test`); (data.status === "gagal" ? toast.error : toast.success)(`Tes ${r.name}: ${data.status}${data.detail ? ` — ${data.detail}` : ""}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Hapus penerima ${r.name}?`)) return;
    try { await api.delete(`/wa/recipients/${r.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader eyebrow="Notifikasi" title="Ringkasan WhatsApp" desc="Ringkasan KPI bulanan + pantauan Bulan Berjalan via Whacenter, ke nomor personal maupun grup.">
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <PeriodSelect value={ym} onChange={setYm} />
            <Button data-testid="wa-send-now-button" onClick={() => sendNow("harian")} disabled={!!sending}>{sending === "harian" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Kirim Harian</Button>
            <Button data-testid="wa-send-monthly-button" variant="outline" onClick={() => sendNow("bulanan")} disabled={!!sending}>{sending === "bulanan" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Kirim Bulanan + PPT</Button>
          </div>
        )}
      </PageHeader>
      {settings && !settings.device_configured && (
        <div data-testid="wa-simulation-banner" className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p><b>Mode simulasi.</b> Device ID Whacenter belum diisi — pesan disusun dan dicatat di log, tetapi tidak benar-benar terkirim.</p>
        </div>
      )}
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          {settings && isAdmin && <DeviceCard settings={settings} onSaved={setSettings} />}
          {settings && <SettingsCard settings={settings} isAdmin={isAdmin} onSaved={setSettings} />}
          {settings && <MonthlyCard settings={settings} isAdmin={isAdmin} onSaved={setSettings} />}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b p-4">
              <h3 className="font-heading text-lg font-semibold">Penerima</h3>
              {isAdmin && <Button data-testid="add-recipient-button" size="sm" variant="outline" onClick={() => setAdding("new")}><Plus className="mr-1 h-4 w-4" />Tambah</Button>}
            </div>
            <ul data-testid="recipients-list" className="divide-y">
              {recipients.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">Belum ada penerima.</li>}
              {recipients.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 p-4 text-sm">
                  <div><p className="font-medium">{r.name} <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${r.type === "grup" ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>{r.type}</span> <span className="font-num text-xs text-muted-foreground">{r.target}</span></p><p className="text-xs text-muted-foreground">{r.uid ? `${r.uid} · ` : ""}{r.units.length ? r.units.join(", ") : "Seluruh unit"}</p><div className="mt-1.5"><KindChips testid={`recipient-report-${r.id}`} value={r.reports} disabled={!isAdmin} onChange={(v) => setReports(r, v)} /></div></div>
                  {isAdmin && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button data-testid={`test-recipient-${r.id}`} size="sm" variant="outline" onClick={() => test(r)}>Tes</Button>
                      <Button data-testid={`edit-recipient-${r.id}`} size="icon" variant="ghost" onClick={() => setAdding(r)}><Pencil className="h-4 w-4" /></Button>
                      <Button data-testid={`delete-recipient-${r.id}`} size="icon" variant="ghost" onClick={() => remove(r)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <Preview ym={ym} />
      </div>
      <FilesCard isAdmin={isAdmin} />
      <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
        <div className="border-b p-4"><h3 className="font-heading text-lg font-semibold">Log Pengiriman</h3></div>
        <table data-testid="wa-logs-table" className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground"><tr><th className="p-3 text-left">Waktu</th><th className="p-3 text-left">Penerima</th><th className="p-3 text-left">Unit</th><th className="p-3 text-left">Pemicu</th><th className="p-3 text-left">Status</th></tr></thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Belum ada pengiriman.</td></tr>}
            {logs.map((l) => (
              <tr key={l.id} className="border-t" title={l.detail || ""}>
                <td className="p-3 whitespace-nowrap">{fmtDate(l.created_at)}</td><td className="p-3">{l.recipient} <span className="font-num text-xs text-muted-foreground">{l.phone}</span></td>
                <td className="p-3">{l.unit || "Seluruh unit"}</td><td className="p-3 capitalize">{l.trigger}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${LOG_CLS[l.status]}`}>{l.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && <RecipientDialog key={adding === "new" ? "new" : adding.id} open initial={adding === "new" ? null : adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}
